/**
 * decisionModels/routing — the route table behind a decision step, and the
 * exact check that every possible answer goes somewhere.
 *
 * Two kinds of question:
 *   yes/no, choice  a finite set of outcomes (yes/no/cant_tell; each option
 *                   + cant_tell). A condition is the outcomes it allows.
 *   score           a number from 0 to the top level (levels - 1). The model
 *                   answers between levels (1.13 on a 0–2 scale), so a
 *                   condition is a range { min, max }: min ≤ value < max,
 *                   and ≤ max when max is the top level.
 *
 * A route row is a box in the answer space (one allowed set or range per
 * question; no condition = any answer). Rows are tried top to bottom and the
 * first match wins; `otherwise` takes the rest. coverage() subtracts each
 * row's box from what is still uncovered — box minus box is at most one
 * piece per question, on sets and on ranges alike — so the result is exact:
 * which rows are never reached and what falls through to `otherwise`.
 *
 * Route shape: { id, name, when: { key: [outcome, ...] | { min, max } } }
 * Table shape: { routes: [route...], otherwise: { name } | null }
 *
 * Twin of backend/services/decisionModels/routing.js; keep the two in step
 * (the tests are mirrored).
 */

const CANT_TELL = 'cant_tell';

const isRange = (q) => !!q && q.type === 'score';
const topOf = (q) => Math.max(1, ((q && q.criteria) || []).length - 1);
const fmt = (n) => (Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100));

/**
 * A choice question's options in the author's order. Stored JSON objects do
 * not keep key order (Postgres jsonb sorts them), so the order travels as
 * `order`; options it does not name follow in key order.
 */
function optionsOf(q) {
  const keys = Object.keys((q && q.criteria) || {});
  const order = Array.isArray(q && q.order) ? q.order.filter((k) => keys.includes(k)) : [];
  return [...order, ...keys.filter((k) => !order.includes(k))];
}

/** Every outcome of a yes/no or choice question, in display order. */
function outcomesOf(q) {
  if (!q) return [];
  if (q.type === 'noul') return ['yes', 'no', CANT_TELL];
  if (q.type === 'choice') return [...optionsOf(q), CANT_TELL];
  return [];
}

function outcomeLabel(q, o) {
  if (o === CANT_TELL) return "can't tell";
  if (isRange(q) && typeof o === 'number') return fmt(o);
  return String(o);
}

/* ── ranges: a list of disjoint intervals { lo, hi, loInc, hiInc } ───────── */

const ivEmpty = (i) => i.lo > i.hi || (i.lo === i.hi && !(i.loInc && i.hiInc));
const clean = (list) => list.filter((i) => !ivEmpty(i));

/** A condition { min, max } on a score question as intervals. */
function rangeOf(q, v) {
  const top = topOf(q);
  if (Array.isArray(v)) {
    // Older tables named levels ('2', '3'): each level is its half-way band.
    return clean(v.filter((x) => /^\d+$/.test(String(x))).map((x) => {
      const l = Number(x);
      return { lo: Math.max(0, l - 0.5), hi: Math.min(top, l + 0.5), loInc: true, hiInc: l + 0.5 >= top };
    }));
  }
  if (!v || typeof v !== 'object') return [{ lo: 0, hi: top, loInc: true, hiInc: true }];
  const min = Number.isFinite(Number(v.min)) ? Math.max(0, Number(v.min)) : 0;
  const max = Number.isFinite(Number(v.max)) ? Math.min(top, Number(v.max)) : top;
  return clean([{ lo: min, hi: max, loInc: true, hiInc: max >= top }]);
}

function ivIntersect(a, b) {
  const out = [];
  for (const x of a) {
    for (const y of b) {
      const lo = Math.max(x.lo, y.lo);
      const hi = Math.min(x.hi, y.hi);
      const loInc = (x.lo === lo ? x.loInc : true) && (y.lo === lo ? y.loInc : true);
      const hiInc = (x.hi === hi ? x.hiInc : true) && (y.hi === hi ? y.hiInc : true);
      out.push({ lo, hi, loInc, hiInc });
    }
  }
  return clean(out);
}

function ivSubtract(a, b) {
  let rest = a.slice();
  for (const y of b) {
    const next = [];
    for (const x of rest) {
      if (!ivIntersect([x], [y]).length) { next.push(x); continue; }
      // What lies left of y, and what lies right of it.
      next.push({ lo: x.lo, hi: y.lo, loInc: x.loInc, hiInc: !y.loInc });
      next.push({ lo: y.hi, hi: x.hi, loInc: !y.hiInc, hiInc: x.hiInc });
    }
    rest = clean(next).filter((i) => i.lo >= 0);
  }
  return rest;
}

/* ── boxes ───────────────────────────────────────────────────────────────── */

/** The space: question key → { range, q, all } where all is the full set or full range. */
function spaceOf(questions) {
  const space = {};
  for (const [k, q] of Object.entries(questions || {})) {
    space[k] = isRange(q)
      ? { range: true, q, all: [{ lo: 0, hi: topOf(q), loInc: true, hiInc: true }] }
      : { range: false, q, all: outcomesOf(q) };
  }
  return space;
}

/**
 * A row's box. No condition on a question = any answer; a set condition with
 * nothing picked matches nothing (the row says so) rather than everything.
 */
function boxOf(space, when) {
  const box = {};
  for (const [k, d] of Object.entries(space)) {
    const v = when ? when[k] : undefined;
    if (v === undefined || v === null) box[k] = d.all.slice();
    else if (d.range) box[k] = ivIntersect(d.all, rangeOf(d.q, v));
    else box[k] = Array.isArray(v) ? v.filter((o) => d.all.includes(o)) : d.all.slice();
  }
  return box;
}

const dimIntersect = (d, a, b) => (d.range ? ivIntersect(a, b) : a.filter((o) => b.includes(o)));
const dimSubtract = (d, a, b) => (d.range ? ivSubtract(a, b) : a.filter((o) => !b.includes(o)));
const isEmpty = (box) => Object.values(box).some((s) => s.length === 0);

function intersect(space, a, b) {
  const out = {};
  for (const k of Object.keys(a)) out[k] = dimIntersect(space[k], a[k], b[k]);
  return out;
}

/** a − b as disjoint boxes. */
function subtract(space, a, b) {
  if (isEmpty(intersect(space, a, b))) return [a];
  const pieces = [];
  let rest = { ...a };
  for (const k of Object.keys(a)) {
    const outside = dimSubtract(space[k], rest[k], b[k]);
    if (outside.length) pieces.push({ ...rest, [k]: outside });
    rest = { ...rest, [k]: dimIntersect(space[k], rest[k], b[k]) };
  }
  return pieces;
}

/** Combinations in a box when every question is a set; null when a range is involved. */
function countOf(space, box) {
  let n = 1;
  for (const [k, s] of Object.entries(box)) {
    if (space[k].range) return null;
    n *= s.length;
  }
  return n;
}

/**
 * The full check.
 * Returns {
 *   total, uncovered_count     combinations (null when a score question makes it a range)
 *   rows: [{ id, never, empty, shadowed_by, overlap_with }],
 *   uncovered: [box],          what reaches `otherwise`
 *   complete                   every answer has a destination
 * }
 */
function coverage(questions, table) {
  const space = spaceOf(questions);
  const keys = Object.keys(space);
  const full = boxOf(space, {});
  let remaining = keys.length ? [full] : [];
  const seen = [];
  const rows = [];
  for (const r of (table && table.routes) || []) {
    const box = boxOf(space, r.when);
    const empty = isEmpty(box);
    const reached = !empty && remaining.some((rem) => !isEmpty(intersect(space, rem, box)));
    const overlap = seen.filter((s) => !isEmpty(intersect(space, s.box, box))).map((s) => s.id);
    rows.push({ id: r.id, empty, never: !reached, shadowed_by: reached ? [] : overlap, overlap_with: reached ? overlap : [] });
    if (!empty) remaining = remaining.flatMap((rem) => subtract(space, rem, box));
    seen.push({ id: r.id, box });
  }
  const total = keys.length ? countOf(space, full) : 0;
  const counts = remaining.map((b) => countOf(space, b));
  const uncovered_count = counts.some((c) => c === null) ? null : counts.reduce((n, c) => n + c, 0);
  const hasOtherwise = !!(table && table.otherwise);
  return { total, rows, uncovered: remaining, uncovered_count, complete: remaining.length === 0 || hasOtherwise };
}

function describeRange(list, top) {
  return list.map((i) => {
    const parts = [];
    if (i.lo > 0 || !i.loInc) parts.push(`${i.loInc ? '≥' : '>'} ${fmt(i.lo)}`);
    if (i.hi < top || !i.hiInc) parts.push(`${i.hiInc ? '≤' : '<'} ${fmt(i.hi)}`);
    return parts.length ? parts.join(' and ') : 'any value';
  }).join(' or ');
}

/** A box as words: "urgent is yes · severity ≥ 1.5". Questions left at "any" are skipped. */
function describeBox(questions, box) {
  const space = spaceOf(questions);
  const parts = [];
  for (const [k, allowed] of Object.entries(box)) {
    const d = space[k];
    if (!d) continue;
    if (d.range) {
      const top = topOf(d.q);
      const full = allowed.length === 1 && allowed[0].lo === 0 && allowed[0].hi === top && allowed[0].loInc && allowed[0].hiInc;
      if (!full) parts.push(`${k} ${describeRange(allowed, top)}`);
    } else if (allowed.length !== d.all.length) {
      parts.push(`${k} is ${allowed.map((o) => outcomeLabel(d.q, o)).join(' or ')}`);
    }
  }
  return parts.length ? parts.join(' · ') : 'any answer';
}

const inRange = (list, v) => list.some((i) => (i.loInc ? v >= i.lo : v > i.lo) && (i.hiInc ? v <= i.hi : v < i.hi));

/** Run time: the first row whose box holds these answers, else otherwise. */
function pickRoute(questions, table, outcomes) {
  const space = spaceOf(questions);
  for (const r of (table && table.routes) || []) {
    const box = boxOf(space, r.when);
    const hit = Object.keys(box).every((k) => (space[k].range
      ? typeof outcomes[k] === 'number' && inRange(box[k], outcomes[k])
      : box[k].includes(outcomes[k])));
    if (hit) return { route: r, otherwise: false };
  }
  if (table && table.otherwise) return { route: table.otherwise, otherwise: true };
  return null;
}

/**
 * "Route by one question": one row per outcome; for a score, one row per
 * level, each the band that rounds to it (level 1 of 0–2 is 0.5 to 1.5).
 */
function routesByQuestion(questions, key) {
  const q = questions[key];
  const slug = (s) => `${key}_${s}`.replace(/[^a-z0-9_]/gi, '_').toLowerCase();
  if (isRange(q)) {
    const top = topOf(q);
    return (q.criteria || []).map((label, l) => ({
      id: slug(`l${l}`),
      name: String(label || `Level ${l}`),
      when: { [key]: { min: l === 0 ? 0 : l - 0.5, max: l === top ? top : l + 0.5 } },
    }));
  }
  return outcomesOf(q).map((o) => ({ id: slug(o), name: outcomeLabel(q, o), when: { [key]: [o] } }));
}

/** Server-side validation of a table against its questions. */
function tableProblems(questions, table) {
  const out = [];
  const routes = (table && table.routes) || [];
  const ids = new Set();
  for (const r of routes) {
    if (!r || typeof r.id !== 'string' || !/^[a-z0-9_]{1,60}$/.test(r.id)) { out.push('every route needs an id (lowercase letters, digits, _)'); continue; }
    if (ids.has(r.id) || r.id === 'otherwise') out.push(`route id "${r.id}" is used twice`);
    ids.add(r.id);
    if (!String(r.name || '').trim()) out.push(`route "${r.id}" needs a name`);
    for (const [k, v] of Object.entries(r.when || {})) {
      const q = questions[k];
      if (!q) { out.push(`route "${r.name || r.id}" refers to a question that no longer exists (${k})`); continue; }
      if (isRange(q)) {
        if (Array.isArray(v)) continue; // older level lists still read as bands
        const min = Number(v && v.min); const max = Number(v && v.max);
        if (!(Number.isFinite(min) && Number.isFinite(max)) || min < 0 || max > topOf(q) || min >= max) {
          out.push(`route "${r.name || r.id}": ${k} needs a range from 0 to ${topOf(q)} with the start below the end`);
        }
      } else {
        const all = outcomesOf(q);
        const bad = (Array.isArray(v) ? v : []).filter((o) => !all.includes(o));
        if (bad.length) out.push(`route "${r.name || r.id}": ${k} has no outcome ${bad.join(', ')}`);
        if (Array.isArray(v) && v.length === 0) out.push(`route "${r.name || r.id}": pick at least one answer for ${k}`);
      }
    }
  }
  const cov = coverage(questions, table);
  if (!cov.complete) {
    const sample = cov.uncovered.slice(0, 3).map((b) => describeBox(questions, b)).join('; ');
    const n = cov.uncovered_count;
    out.push(`${n == null ? 'Some answers' : `${n} answer combination${n === 1 ? '' : 's'}`} go nowhere (${sample}). Add a route or an Otherwise.`);
  }
  return out;
}

export {
  CANT_TELL,
  optionsOf,
  isRange,
  topOf,
  outcomesOf,
  outcomeLabel,
  rangeOf,
  spaceOf,
  boxOf,
  subtract,
  coverage,
  describeBox,
  pickRoute,
  routesByQuestion,
  tableProblems,
};
